<template>
    <Card :pageTitle="pageTitle">
        <div v-if="loading">Loading...</div>
        <div v-else-if="!safeDrawer">Could not load details!</div>
        <div v-else>
            <div class="mb-0">
                <Button noMargin @click="$router.push(`/cash-management/${isSafe ? safeDrawer.id : safeDrawer.parent}`)">
                    <i class="fas fa-chevron-left"></i> Back To Safe List
                </Button>
            </div>
            <div class="drawer-detail-header">
                <div>
                    <div class="drawer-title">
                        {{ safeDrawer.name }}
                    </div>
                    <p class="text-secondary" v-if="safeDrawer.lastClosedAt || safeDrawer.lastOpenedAt">
                        <i>{{ safeDrawerOpenStatus }}</i>
                    </p>
                    <div v-if="isDrawer">
                        <!-- todo last open date -->
                        <!-- <p class="text-secondary">Created on </p> -->
                    </div>
                </div>
                <div>
                    <!-- Delete button: only show for closed drawers -->
                    <Button v-if="isDrawer && !safeDrawer.isOpen" @click="deleteDrawer" borderOnly class="me-2">Delete</Button>
                    
                    <!-- For SAFES: Show Transfer button when open, Open/Close based on status -->
                    <Button v-if="isSafe && safeDrawer.isOpen" @click="$router.push(`/safe-drawers/${safeDrawer.id}/transfer`)" borderOnly class="me-2">
                        <i class="fas fa-exchange-alt"></i> Transfer Cash
                    </Button>
                    <Button v-if="isSafe && !safeDrawer.isOpen" @click="openSafe">Open Safe</Button>
                    <Button v-if="isSafe && safeDrawer.isOpen" @click="closeSafe">Close Safe</Button>
                    
                    <!-- For DRAWERS: Show Cash In when closed, Cash Out when open -->
                    <Button v-if="isDrawer && !safeDrawer.isOpen" @click="openSafe">Cash In</Button>
                    <Button v-if="isDrawer && safeDrawer.isOpen" @click="closeSafe">Cash Out</Button>
                </div>
            </div>
            <div class="row">
                <div class="col-md-3">
                    <DetailContainer sm :title="`${safeDrawerTitle} Summary`">
                        <div v-if="safeDrawerSummary">
                            <!-- COVA-STYLE: On-Hand Breakdown -->
                            <div style="margin: 15px 0; padding: 10px; background-color: #e3f2fd; border-radius: 5px; border-left: 4px solid #2196f3;">
                                <div style="font-weight: bold; margin-bottom: 8px; color: #1565c0; font-size: 14px;">💵 Total On Premises</div>
                                <InfoPricedValue 
                                    noColor 
                                    sideBySide 
                                    title="Total" 
                                    :value="safeDrawerSummary.totalOnPremisesCash || 0" 
                                    style="font-size: 1.1em; font-weight: 600;"
                                />
                            </div>

                            <div style="margin: 15px 0; padding: 10px; background-color: #e8f5e9; border-radius: 5px; border-left: 4px solid #4caf50;">
                                <div style="font-weight: bold; margin-bottom: 8px; color: #2e7d32; font-size: 14px;">💰 Breakdown</div>
                                <InfoPricedValue 
                                    noColor 
                                    sideBySide 
                                    title="Safe On Hand" 
                                    :value="safeDrawerSummary.safeOnHandCash || 0" 
                                />
                                <InfoPricedValue 
                                    noColor 
                                    sideBySide 
                                    title="Drawers On Hand" 
                                    :value="safeDrawerSummary.drawersOnHandCash || 0" 
                                />
                            </div>

                            <!-- Historical Totals Section -->
                            <div style="margin: 15px 0; padding: 10px; background-color: #fff3e0; border-radius: 5px; border-left: 4px solid #ff9800;">
                                <div style="font-weight: bold; margin-bottom: 8px; color: #e65100; font-size: 14px;">📜 Historical Totals (All Time)</div>
                                <InfoPricedValue 
                                    noColor 
                                    sideBySide 
                                    title="Cash" 
                                    :value="safeDrawerSummary.historicalTotals ? safeDrawerSummary.historicalTotals.cash : safeDrawerSummary.totalCashAmount" 
                                />
                                <InfoPricedValue 
                                    noColor 
                                    sideBySide 
                                    title="Non-Cash" 
                                    :value="safeDrawerSummary.historicalTotals ? safeDrawerSummary.historicalTotals.nonCash : safeDrawerSummary.totalNonCashAmount" 
                                />
                                <InfoPricedValue 
                                    noColor 
                                    sideBySide 
                                    title="Total" 
                                    :value="safeDrawerSummary.historicalTotals ? safeDrawerSummary.historicalTotals.total : (safeDrawerSummary.totalCashAmount + safeDrawerSummary.totalNonCashAmount)" 
                                />
                            </div>
                            
                            <!-- Session Data (Current Activity) -->
                            <div style="margin: 15px 0; padding: 10px; background-color: #f8f9fa; border-radius: 5px;">
                                <div style="font-weight: bold; margin-bottom: 8px; color: #495057;">📊 This Session</div>
                                <InfoListItem
                                    sideBySide
                                    title="Sales"
                                    :value="String(safeDrawerSummary.sessionSalesCount || 0)"
                                />
                                <InfoListItem
                                    sideBySide
                                    title="Refunds"
                                    :value="String(safeDrawerSummary.sessionRefundsCount || 0)"
                                />
                            </div>
                            
                            <!-- All-Time Data (Historical) -->
                            <div style="margin: 15px 0; padding: 10px; background-color: #f0f8ff; border-radius: 5px;">
                                <div style="font-weight: bold; margin-bottom: 8px; color: #495057;">📈 All Time ({{ safeDrawer.name }})</div>
                                <InfoListItem
                                    sideBySide
                                    title="Sales"
                                    :value="String(safeDrawerSummary.allTimeSalesCount || safeDrawerSummary.totalSalesCount || 0)"
                                />
                                <InfoListItem
                                    sideBySide
                                    title="Refunds"
                                    :value="String(safeDrawerSummary.allTimeRefundsCount || safeDrawerSummary.totalRefundsCount || 0)"
                                />
                            </div>
                            
                            <!-- Total Transactions -->
                            <InfoListItem 
                                sideBySide 
                                title="Total Transactions" 
                                :value="String(safeDrawerSummary.allTimeTransactions || safeDrawerSummary.totalTransactions || safeDrawerSummary.totalCashManagementTransactions || 0)"
                            />
                        </div>
                    </DetailContainer>
                    <DetailContainer sm title="Last Cash Out">
                        <div v-if="lastCashOutSnapshot">
                            <p class="text-secondary snapshot-date" v-if="lastCashOutSnapshot.createdAtFormatted1">
                                Closed on {{ lastCashOutSnapshot.createdAtFormatted1 }}
                            </p>
                            <div class="snapshot-totals">
                                <InfoPricedValue noColor sideBySide title="Counted Total" :value="lastCashOutSnapshot.totalAmount || 0" />
                                <InfoPricedValue noColor sideBySide title="Counted Cash" :value="lastCashOutSnapshot.totalCashAmount || 0" />
                                <InfoPricedValue noColor sideBySide title="Counted Non-Cash" :value="lastCashOutSnapshot.totalNonCashAmount || 0" />
                                <InfoPricedValue noColor sideBySide title="Transferred Total" :value="lastCashOutSnapshot.totalTransferredAmount || lastCashOutSnapshot.totalAmount || 0" />
                                <InfoPricedValue noColor sideBySide title="Cash Transferred" :value="lastCashOutSnapshot.cashTransferredAmount || lastCashOutSnapshot.totalCashAmount || 0" />
                                <InfoPricedValue noColor sideBySide title="Cash Retained" :value="lastCashOutSnapshot.cashRetainedAmount || 0" />
                                <InfoPricedValue noColor sideBySide title="Retained Float Target" :value="lastCashOutSnapshot.retainedFloatTarget || 0" />
                            </div>
                            <table class="table table-sm snapshot-table" v-if="filteredLastCashOutPayments.length">
                                <thead>
                                    <tr>
                                        <th>Payment Method</th>
                                        <th class="text-end">Counted</th>
                                        <th class="text-end">Transferred</th>
                                        <th class="text-end">Retained</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="payment in filteredLastCashOutPayments"
                                        :key="payment.modeOfPaymentId || payment.modeOfPaymentName"
                                    >
                                        <td>{{ payment.modeOfPaymentName || "Payment" }}</td>
                                        <td class="text-end">{{ formatCurrency(payment.value) }}</td>
                                        <td class="text-end">{{ formatCurrency(payment.transferredValue) }}</td>
                                        <td class="text-end">{{ formatCurrency(payment.retainedValue) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <p v-else class="text-muted small mb-0">
                                No payment breakdown recorded.
                            </p>
                            <p class="section-title mt-3" style="font-size: 1rem;">Cash Denominations</p>
                            <table class="table table-sm snapshot-table" v-if="lastCashOutDenominations.length">
                                <thead>
                                    <tr>
                                        <th>Denomination</th>
                                        <th class="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="denom in lastCashOutDenominations"
                                        :key="`${denom.amount}-${denom.qty}`"
                                    >
                                        <td>{{ denom.qty }} x {{ formatCurrency(denom.amount) }}</td>
                                        <td class="text-end">{{ formatCurrency(denom.total) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <p v-else class="text-muted small mb-0">
                                No denomination data recorded.
                            </p>
                            <p v-if="lastCashOutSnapshot.notes" class="text-secondary snapshot-notes">
                                Notes: {{ lastCashOutSnapshot.notes }}
                            </p>
                        </div>
                        <div v-else class="text-muted small">
                            This {{ safeDrawerTitle.toLowerCase() }} has not been cashed out yet.
                        </div>
                    </DetailContainer>
                </div>
                <div class="col-md-9">
                    <p class="section-title">General</p>
                    <EditBtn v-model="editMode" />

                    <div style="max-width: 500px; margin-top: 15px">
                        <InfoListItem :title="`${safeDrawerTitle} Name`" :value="name" :editMode="editMode" />
                        <CustomInput v-model="name" v-if="editMode" />
                        <InfoListItem :title="`${safeDrawerTitle} Description`" :value="description" :editMode="editMode" />
                        <CustomInput v-model="description" v-if="editMode" />
                        <div v-if="editMode">
                            <Button @click="cancelEditing" class="me-2" borderOnly> Cancel </Button>
                            <Button @click="updateSafeDrawer">{{ updatingSafeDrawer ? "Please wait..." : "Save Changes" }}</Button>
                        </div>
                    </div>
                    <hr class="my-4" />

                    <DrawerActivityTable v-if="safeDrawer.id" :safe-drawer-id="safeDrawer.id" />
                </div>
            </div>
        </div>
    </Card>
</template>

<script>
import Card from "../../components/UI/Card.vue"
import CashManagementFetchApi from "@/utils/fetch/cash-management-fetch-api"
import InfoListItem from "../../components/UI/InfoListItem.vue"
import DetailContainer from "../../components/layout/DetailContainer.vue"
import CustomInput from "../../components/crud/custom-input.vue"
import Button from "../../components/UI/Button.vue"
import EditBtn from "../../components/UI/EditBtn.vue"
import { confirmAction, errMsg, usDateFormat } from "../../utils/common"
import { http } from "../../utils/http"
import InfoPricedValue from "../../components/UI/InfoPricedValue.vue"
import DrawerActivityTable from "../../components/cash-management/DrawerActivityTable.vue"

export default {
    components: {
        InfoPricedValue,
        EditBtn,
        Button,
        DetailContainer,
        Card,
        InfoListItem,
        CustomInput,
        DrawerActivityTable,
    },
    data() {
        return {
            pageTitle: "Details",
            safeDrawer: null,
            loading: false,
            isSafe: false,
            isDrawer: false,
            safeDrawerTitle: "",
            editMode: false,
            name: "",
            description: "",
            updatingSafeDrawer: false,
            parentSafe: null,
            childDrawers: null,
            safeDrawerSummary: null,
            lastCashOutSnapshot: null,
        }
    },
    async mounted() {
        this.initSafeDrawer()
        this.initSafeDrawerSummary()
    },
    computed: {
        safeDrawerOpenStatus() {
            return this.safeDrawer.isOpen
                ? this.isSafe
                    ? `Last opened on ${this.safeDrawer.lastOpenedAtFormatted2}`
                    : `Last cashed in ${this.safeDrawer.lastOpenedAtFormatted2}`
                : this.isSafe
                ? `Last closed on ${this.safeDrawer.lastClosedAtFormatted2}`
                : `Last cashed out on ${this.safeDrawer.lastClosedAtFormatted2}`
        },
        // Enable debug mode via URL parameter: ?debug=true
        debugMode() {
            return this.$route.query.debug === 'true'
        },
        lastCashOutPayments() {
            const payments = this.lastCashOutSnapshot && this.lastCashOutSnapshot.paymentModes
            return Array.isArray(payments) ? payments : []
        },
        filteredLastCashOutPayments() {
            return this.lastCashOutPayments.filter(
                (item) =>
                    Number(item?.value || 0) > 0 ||
                    Number(item?.transferredValue || 0) > 0 ||
                    Number(item?.retainedValue || 0) > 0
            )
        },
        lastCashOutDenominations() {
            const rows = this.lastCashOutSnapshot && this.lastCashOutSnapshot.billValues
            return Array.isArray(rows) ? rows : []
        },
    },
    methods: {
        usDateFormat,
        formatCurrency(value) {
            const amount = Number(value || 0)
            return `$${amount.toFixed(2)}`
        },
        openSafe() {
            // ignore name, its for both safe & drawer
            let success = false
            if (this.parentSafe) {
                if (!this.parentSafe.isOpen) {
                    return this.$toast.error("Safe must be open to cash in!")
                }
                success = true
            }
            if (this.isSafe || success) {
                this.$router.push(`/safe-drawers/${this.safeDrawer.id}/open`)
            }
        },
        closeSafe() {
            let success = false
            // ignore name, its for both safe & drawer
            if (this.childDrawers) {
                if (this.childDrawers.find((item) => item.isOpen)) {
                    return this.$toast.error("All drawers must be cashed out to close safe!")
                }
                success = true
            }
            if (this.isDrawer || success) {
                this.$router.push(`/safe-drawers/${this.safeDrawer.id}/close`)
            }
        },
        async deleteDrawer() {
            if (!this.safeDrawer) return
            await confirmAction(
                "Deleted",
                "Drawer Deleted Successfully!",
                async () => {
                    await http.delete(`/safe-drawers/${this.safeDrawer.id}`)
                },
                "Are you sure to delete drawer?"
            )
            setTimeout(() => {
                window.location.href = `/cash-management/${this.isSafe ? this.safeDrawer.id : this.safeDrawer.parent}`
            }, 1000)
        },
        async initSafeDrawerSummary() {
            this.loadingSummary = true
            try {
                const { result } = await CashManagementFetchApi.fetchSafeOrDrawerSummary(this.$route.params.id, this.debugMode)
                this.safeDrawerSummary = result
                
                // Only log debug info when explicitly requested via ?debug=true
                if (this.debugMode) {
                    console.log('=== DRAWER SUMMARY DEBUG ===')
                    console.log('API Response:', result)
                    console.log('📊 Session Counts:')
                    console.log('  Sales:', result.sessionSalesCount)
                    console.log('  Refunds:', result.sessionRefundsCount)
                    console.log('  Transactions:', result.sessionTransactions)
                    console.log('📈 All-Time Counts:')
                    console.log('  Sales:', result.allTimeSalesCount)
                    console.log('  Refunds:', result.allTimeRefundsCount)
                    console.log('  Transactions:', result.allTimeTransactions)
                    console.log('💰 Amount Data:')
                    console.log('  Session Sales Amount:', result.sessionSaleAmount)
                    console.log('  All-Time Sales Amount:', result.allTimeSaleAmount)
                    if (result.debug) {
                        console.log('🔍 Debug Info:', result.debug)
                    }
                    console.log('=== END DEBUG ===')
                }
            } catch (err) {
                console.log("error in loading safe drawer summary data", err)
                this.$toast.error("Could not safe drawer summary!")
            }
            this.loadingSummary = false
        },
        async initSafeDrawer() {
            this.loading = true
            try {
                const { result, childDrawers, parentData, lastCashOutSnapshot } = await CashManagementFetchApi.fetchSafeOrDrawerDetail(
                    this.$route.params.id,
                    true
                )
                this.safeDrawer = result
                this.parentSafe = parentData // will have value if its a drawer
                this.childDrawers = childDrawers // will have value if its a safe
                this.lastCashOutSnapshot = lastCashOutSnapshot

                this.name = this.safeDrawer.name
                this.description = this.safeDrawer.description

                this.isSafe = !this.safeDrawer.parent
                this.isDrawer = !this.isSafe
                this.safeDrawerTitle = this.isSafe ? "Safe" : "Drawer"
                this.pageTitle = this.isSafe ? "Safe Details" : "Drawer Details"
            } catch (err) {
                console.log("error in loading data", err)
                this.$toast.error("Could not load details!")
            }

            this.loading = false
        },
        async updateSafeDrawer() {
            this.updatingSafeDrawer = true
            try {
                await CashManagementFetchApi.updateSafeDrawer(this.$route.params.id, {
                    name: this.name,
                    description: this.description,
                })
                this.safeDrawer.name = this.name
                this.safeDrawer.description = this.description
                this.$toast.success("Saved changes!")
                this.editMode = false
            } catch (err) {
                console.log("error in updating safe drawer", err)
                this.$toast.error(errMsg(err, "Could not save data!"))
            }
            this.updatingSafeDrawer = false
        },
        cancelEditing() {
            this.editMode = false
            this.name = this.safeDrawer.name
            this.description = this.safeDrawer.description
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.drawer-title {
    font-size: 1.4em;
}
.section-title {
    font-size: 1.4em;
    color: #444;
}
.drawer-detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: $sectionGap;
}

.snapshot-table th,
.snapshot-table td {
    font-size: 0.9em;
}
.snapshot-date,
.snapshot-notes {
    font-size: 0.85em;
}
.snapshot-notes {
    margin-top: 8px;
}
</style>
